import { useEffect, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { MEDIA_QUERIES } from "./mediaQueries";

/**
 * ET view-mode override. By default ('auto') the editor picks its layout from the DEVICE:
 * a touch device (phone/tablet — any coarse pointer) gets the compact mobile layout, a computer
 * (mouse/trackpad) gets the full spreadsheet. The landing-page demos (Letters K/O) rely on this:
 * they embed the ET in a narrow iframe to preview the phone layout, so 'auto' must stay the
 * default — forcing 'full' would cram the wide spreadsheet into the phone frame.
 * A user — including anyone opening a shared /s/ link — can force 'phone' or 'full' with the
 * on-screen toggle; the choice is remembered per browser and applied live.
 */
const KEY = "cc_et_view"; // 'auto' | 'phone' | 'full'
export const ET_VIEW_EVENT = "cc-et-view-changed";

export function getEtViewMode() {
  try {
    const v = localStorage.getItem(KEY);
    return v === "phone" || v === "full" ? v : "auto";
  } catch (e) {
    return "auto";
  }
}

export function setEtViewMode(mode) {
  try { localStorage.setItem(KEY, mode); } catch (e) { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent(ET_VIEW_EVENT, { detail: mode })); } catch (e) { /* ignore */ }
}

/** Reactive [mode, setMode] — re-renders when the toggle changes anywhere. */
export function useEtViewMode() {
  const [mode, setMode] = useState(getEtViewMode());
  useEffect(() => {
    const on = (e) => setMode((e && e.detail) || getEtViewMode());
    window.addEventListener(ET_VIEW_EVENT, on);
    return () => window.removeEventListener(ET_VIEW_EVENT, on);
  }, []);
  return [mode, setEtViewMode];
}

/**
 * Effective responsive breakpoints, honoring the override. 'auto' returns the real media
 * queries (unchanged behavior); 'phone' forces the compact layout; 'full' forces every column.
 */
const COMPACT = { isXSmallScreen: true, isSmallScreen: true, isMediumScreen: true, isLargeScreen: false };
const FULL = { isXSmallScreen: false, isSmallScreen: false, isMediumScreen: false, isLargeScreen: true };

export function useEtBreakpoints() {
  const [mode] = useEtViewMode();
  const xs = useMediaQuery(MEDIA_QUERIES.xs);
  const sm = useMediaQuery(MEDIA_QUERIES.sm);
  const md = useMediaQuery(MEDIA_QUERIES.md);
  const lg = useMediaQuery(MEDIA_QUERIES.lg);
  // Any touch device (primary pointer is coarse) — phone or tablet — defaults to the compact
  // layout, INDEPENDENT of the reported viewport width. That way "Request Desktop Site" (which
  // only widens the reported width, not the pointer type) can't fool a phone into the wide
  // spreadsheet. A computer (mouse/trackpad → fine pointer) always gets Full. Users tap the
  // toggle to override either way.
  const touchDevice = useMediaQuery('(pointer: coarse)');

  if (mode === "phone") return { ...COMPACT, viewMode: mode };
  if (mode === "full") return { ...FULL, viewMode: mode };
  // auto: phones / touch devices (or a genuinely tiny window) default to compact
  if (xs || touchDevice) return { ...COMPACT, viewMode: mode };
  return { isXSmallScreen: xs, isSmallScreen: sm, isMediumScreen: md, isLargeScreen: lg, viewMode: mode };
}

/**
 * Compact-view PRESET MODES. Instead of a wall of individual toggles, the phone layout offers a
 * few named looks you cycle through with an arrow. Each mode is a combination of the same
 * under-thumbnail options (download / timecode / big chip / big thumbnail / wrap text) we've been
 * trying. "Cards" is the recommended most-mobile-friendly one and is the default.
 */
export const ET_MODES = [
  { key: "compact", name: "Compact", hint: "Thumbnail + text, with download & adjustable timecode under each",
    extras: { download: true, timecode: true, bigLetters: true, bigThumb: false, narrowText: false } },
  { key: "simple", name: "Simple", hint: "Just thumbnail + text — swipe right for the adjustable timecode column",
    extras: { download: false, timecode: false, bigLetters: false, bigThumb: false, narrowText: false } },
  { key: "media", name: "Media", hint: "Bigger thumbnail, download & adjustable timecode under each",
    extras: { download: true, timecode: true, bigLetters: true, bigThumb: true, narrowText: false } },
];
const DEFAULT_MODE = 0; // "Compact" — first + default on phones
const MODE_KEY = "cc_et_mode";
export const ET_MODE_EVENT = "cc-et-mode-changed";

export function getEtModeIndex() {
  try {
    const n = parseInt(localStorage.getItem(MODE_KEY), 10);
    return Number.isInteger(n) && n >= 0 && n < ET_MODES.length ? n : DEFAULT_MODE;
  } catch (e) {
    return DEFAULT_MODE;
  }
}

export function setEtModeIndex(i) {
  const idx = ((i % ET_MODES.length) + ET_MODES.length) % ET_MODES.length;
  try { localStorage.setItem(MODE_KEY, String(idx)); } catch (e) { /* ignore */ }
  try { window.dispatchEvent(new CustomEvent(ET_MODE_EVENT, { detail: idx })); } catch (e) { /* ignore */ }
  return idx;
}

export function cycleEtMode(dir = 1) {
  return setEtModeIndex(getEtModeIndex() + dir);
}

/** Reactive current mode [index, modeObject]. */
export function useEtMode() {
  const [idx, setIdx] = useState(getEtModeIndex());
  useEffect(() => {
    const on = (e) => setIdx(typeof (e && e.detail) === "number" ? e.detail : getEtModeIndex());
    window.addEventListener(ET_MODE_EVENT, on);
    return () => window.removeEventListener(ET_MODE_EVENT, on);
  }, []);
  return [idx, ET_MODES[idx]];
}

/** The extras flags for the current mode. Reactive. */
export function getEtExtras() {
  return { ...ET_MODES[getEtModeIndex()].extras };
}

export function useEtExtras() {
  const [extras, setExtras] = useState(getEtExtras());
  useEffect(() => {
    const on = () => setExtras(getEtExtras());
    window.addEventListener(ET_MODE_EVENT, on);
    return () => window.removeEventListener(ET_MODE_EVENT, on);
  }, []);
  return extras;
}
